import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import {
  STAY_PLAN_TABLES,
  type RecurringUsageRule,
  type StayPlanKind,
  type StayPlanModule,
} from "@/lib/daycare-hotel/stayPlans";

export interface StayPlan {
  id: string;
  module: StayPlanModule;
  name: string;
  kind: StayPlanKind;
  price: number;
  isActive: boolean;
  includedDays: number | null;
  usageRule: RecurringUsageRule | null;
  usageLimit: number | null;
  /** Só plano semanal de creche: 2 = a cada 15 dias. */
  weekInterval?: 1 | 2;
  /**
   * Preço do pacote para quem já tem plano ativo no OUTRO módulo (creche paga
   * menos se já tem hotel, e vice-versa). Só existe em pacotes (day_package).
   * Nulo = a clínica cobra o mesmo preço dos dois.
   */
  priceWithOtherModulePlan: number | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PlanRow = any;

export function normalizeStayPlan(row: PlanRow, module: StayPlanModule): StayPlan {
  return {
    id: row.id,
    module,
    name: row.name,
    kind: row.kind,
    price: Number(row.price),
    isActive: row.is_active,
    includedDays: row.day_package?.included_days ?? null,
    usageRule: row.recurring_rule?.usage_rule ?? null,
    usageLimit: row.recurring_rule?.usage_limit ?? null,
    weekInterval: Number(row.recurring_rule?.week_interval ?? 1) === 2 ? 2 : 1,
    priceWithOtherModulePlan: (module === "hotel" ? row.price_with_daycare_plan : row.price_with_hotel_plan) ?? null,
  };
}

export function stayPlanSavePayload(input: Omit<StayPlan, "id"> & { id?: string }, clinicId: string) {
  return {
    clinic_id: clinicId,
    id: input.id ?? null,
    name: input.name,
    kind: input.kind,
    price: input.price,
    is_active: input.isActive,
    ...(input.kind === "day_package"
      ? { included_days: input.includedDays }
      : {
          usage_rule: input.usageRule,
          ...(input.usageLimit !== null ? { usage_limit: input.usageLimit } : {}),
          ...(input.module === "daycare" && input.usageRule === "per_week" ? { week_interval: input.weekInterval ?? 1 } : {}),
        }),
    // O preço diferenciado por plano do outro módulo só existe em pacotes.
    ...(input.kind === "day_package"
      ? {
          [input.module === "hotel" ? "price_with_daycare_plan" : "price_with_hotel_plan"]:
            input.priceWithOtherModulePlan ?? null,
        }
      : {}),
  };
}

export const stayPlansKey = (module: StayPlanModule | null, clinicId: string | null) => ["stay-plans", module, clinicId];

export async function fetchStayPlans(module: StayPlanModule, clinicId: string): Promise<StayPlan[]> {
  const tables = STAY_PLAN_TABLES[module];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from(tables.plans)
    .select(
      `id, name, kind, price, is_active, ` +
      (module === "hotel" ? `price_with_daycare_plan, ` : `price_with_hotel_plan, `) +
      `day_package:${tables.dayPackages}(included_days), ` +
      // Só a creche tem plano quinzenal; a tabela do hotel nem tem a coluna.
      `recurring_rule:${tables.recurringRules}(usage_rule, usage_limit${module === "daycare" ? ", week_interval" : ""})`,
    )
    .eq("clinic_id", clinicId)
    .is("deleted_at", null)
    .order("is_active", { ascending: false })
    .order("name");
  if (error) throw error;
  return ((data ?? []) as PlanRow[]).map((row) => normalizeStayPlan(row, module));
}

export function useStayPlans(module: StayPlanModule | null) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: stayPlansKey(module, clinicId),
    enabled: Boolean(clinicId && module),
    queryFn: () => fetchStayPlans(module as StayPlanModule, clinicId as string),
  });
}

export function useSaveStayPlan(module: StayPlanModule) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Omit<StayPlan, "id"> & { id?: string }) => {
      if (!clinicId) throw new Error("no_clinic_selected");
      const rpc = module === "daycare" ? "save_daycare_plan" : "save_hotel_plan";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc(rpc, { p_input: stayPlanSavePayload(input, clinicId) });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayPlansKey(module, clinicId) });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export interface DeleteStayPlanInput {
  id: string;
}

export async function deleteStayPlan(module: StayPlanModule, input: DeleteStayPlanInput): Promise<void> {
  const rpc = module === "daycare" ? "delete_daycare_plan" : "delete_hotel_plan";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc(rpc, { p_plan_id: input.id });
  if (error) throw error;
}

export function useDeleteStayPlan(module: StayPlanModule) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: DeleteStayPlanInput) => deleteStayPlan(module, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayPlansKey(module, clinicId) });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
}
